import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { connect } from 'react-redux';
import FileExplorerContext from '../../../contexts/FileExplorerContext';
import { Add, Close, Document, Folder } from 'react-ionicons';
import './UploadElementModal.scss';
import { importElements } from '../../../api/actions/Documents';
import { toast } from 'react-toastify';
import { getOctetsValueFromString } from '../../../helpers/Drive';

const FILE_SIZE_LIMIT = 512 * 1000 * 1000;

const UploadElementModal = ({
    show,
    onClose,
    uploadType,
    session,
    preUploadFiles,
}) => {
    const { fullPath, onOpenFolder, fetchFolderArchitecture } =
        useContext(FileExplorerContext);

    const hiddenFileInput = useRef(null);

    const [files, setFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const selectedFolderName = useMemo(() => {
        if (!files || files.length === 0 || uploadType !== 'folder') {
            return null;
        }

        const firstFile = files[0];
        const firstFolder = firstFile.webkitRelativePath.split('/')[0];

        return firstFolder;
    }, [uploadType, files]);

    const fileMaxSize = useMemo(() => {
        if (!session?.modules?.drive?.uploadSizeLimit) {
            return {
                strValue: '512 MB',
                numValue: FILE_SIZE_LIMIT,
            };
        }
        const uploadSizeLimit = session.modules?.drive?.uploadSizeLimit;

        return {
            strValue: uploadSizeLimit,
            numValue: getOctetsValueFromString(uploadSizeLimit),
        };
    }, [session]);

    useEffect(() => {
        if (!show) {
            setFiles([]);
        }
    }, [show]);

    useEffect(() => {
        if (preUploadFiles) {
            setFiles(preUploadFiles);
        }
    }, [preUploadFiles]);

    const onImportFile = useCallback(
        (event) => {
            const fileUploaded = Array.from(event.target.files);
            const oversizeFiles = fileUploaded.filter(
                (file) => file.size > fileMaxSize.numValue
            );

            if (oversizeFiles && oversizeFiles.length > 0) {
                oversizeFiles.forEach((oversizeFile) => {
                    toast.error(
                        `File ${oversizeFile.name} is above the limit of ${fileMaxSize.strValue}.`,
                        {
                            className: 'custom-toast',
                        }
                    );
                });
                return;
            }

            setFiles([...files, ...fileUploaded]);
        },
        [files, fileMaxSize]
    );

    const onImportClick = useCallback(() => {
        hiddenFileInput.current.click();
    }, []);

    const onResetFiles = useCallback(() => {
        setFiles([]);
    }, []);

    const readDirectory = useCallback(
        (entry, newFiles) => {
            const dirReader = entry.createReader();
            dirReader.readEntries((entries) => {
                entries.forEach((subEntry) => {
                    if (subEntry.isFile) {
                        subEntry.file((file) => {
                            if (file.size > fileMaxSize.numValue) {
                                toast.error(
                                    `File ${file.name} is above the limit of ${fileMaxSize.strValue}.`,
                                    {
                                        className: 'custom-toast',
                                    }
                                );
                            } else {
                                const item = {
                                    file,
                                    webkitRelativePath: `${entry.fullPath.substring(
                                        1
                                    )}/${file.name}`,
                                };

                                setFiles((prevFiles) =>
                                    prevFiles ? [...prevFiles, item] : [item]
                                );
                            }
                        });
                    } else if (subEntry.isDirectory) {
                        readDirectory(subEntry, newFiles);
                    }
                });
            });
        },
        [fileMaxSize]
    );

    const onDrop = useCallback(
        (e) => {
            e.preventDefault();

            const items = e.dataTransfer.items;
            const newFiles = [];

            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                const entry = item.webkitGetAsEntry();

                if (entry) {
                    if (entry.isFile) {
                        entry.file((file) => {
                            if (file.size > fileMaxSize.numValue) {
                                toast.error(
                                    `File ${file.name} is above the limit of ${fileMaxSize.strValue}.`,
                                    {
                                        className: 'custom-toast',
                                    }
                                );
                            } else {
                                newFiles.push(file);
                                setFiles((prevFiles) =>
                                    prevFiles ? [...prevFiles, file] : [file]
                                );
                            }
                        });
                    } else if (entry.isDirectory) {
                        readDirectory(entry, newFiles);
                    }
                }
            }
        },
        [readDirectory, fileMaxSize]
    );

    const onDrag = useCallback((event) => {
        event.preventDefault();
    }, []);

    const onRemoveFile = useCallback(
        (index) => {
            const updatedFiles = [...files];

            updatedFiles.splice(index, 1);
            setFiles(updatedFiles);
        },
        [files]
    );

    const splitFiles = useCallback((arr, chunkSize) => {
        let result = [];
        for (let i = 0; i < arr.length; i += chunkSize) {
            result.push(arr.slice(i, i + chunkSize));
        }
        return result;
    }, []);

    const onUploadFiles = useCallback(async () => {
        if (!files || files.length === 0) {
            return;
        }

        setIsLoading(true);

        try {
            const currentFolderId =
                fullPath && fullPath.length > 0
                    ? fullPath[fullPath.length - 1].id
                    : '';
            const chunkedFiles = splitFiles(files, 20);

            await Promise.all(
                chunkedFiles.map(async (fileArr) => {
                    const formData = new FormData();

                    formData.append('parentId', currentFolderId);

                    if (session.tagsId) {
                        session.tagsId.forEach((tag) => {
                            formData.append('metadata[]', tag.tagNameId);
                        });
                    }

                    fileArr.forEach((file) => {
                        formData.append(
                            'files[]',
                            file.file ? file.file : file
                        );
                        formData.append('filePaths[]', file.webkitRelativePath);
                    });

                    const response = await importElements(formData);

                    return response;
                })
            );

            onOpenFolder(currentFolderId);

            if (uploadType === 'folder') {
                fetchFolderArchitecture();
            }

            onClose();
        } catch (error) {
            console.error(error);
            toast.error(
                `Something went wrong while uploading the ${uploadType}. Please try again.`,
                {
                    className: 'custom-toast',
                }
            );
        } finally {
            setIsLoading(false);
        }
    }, [
        fullPath,
        files,
        onClose,
        onOpenFolder,
        fetchFolderArchitecture,
        uploadType,
        session,
        splitFiles,
    ]);

    return (
        <Modal show={show} onHide={onClose}>
            <Modal.Header closeButton>Upload {uploadType}</Modal.Header>
            <Modal.Body>
                {files.length === 0 && (
                    <div
                        className="rounded p-5"
                        style={{
                            border: '1px dashed #999',
                            cursor: 'pointer',
                        }}
                        onDragOver={onDrag}
                        onDrop={onDrop}
                        onClick={onImportClick}
                    >
                        <p className="font-weight-bold  text-center">
                            <span
                                style={{
                                    fontWeight: 700,
                                }}
                            >
                                Import {uploadType}
                            </span>{' '}
                            or drag & drop here ({fileMaxSize.strValue} max)
                        </p>
                    </div>
                )}

                {files.length > 0 && (
                    <>
                        {uploadType === 'file' ? (
                            <div
                                style={{ maxHeight: '60vh', overflow: 'auto' }}
                            >
                                <span>Imported files:</span>
                                {files.map((file, index) => (
                                    <div
                                        key={`imported-${index}`}
                                        className="border rounded d-flex align-items-center justify-content-between p-2 mt-2"
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="folder-icon">
                                                <Document color="inherit" />
                                            </div>
                                            <span>
                                                {file.file
                                                    ? file.file.name
                                                    : file.name}
                                            </span>
                                        </div>
                                        <Close
                                            color="gray"
                                            className="cursor-pointer"
                                            onClick={() => onRemoveFile(index)}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div>
                                <span>Imported folder:</span>
                                <div className="border rounded d-flex align-items-center justify-content-between p-2 mt-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="folder-icon">
                                            <Folder color="inherit" />
                                        </div>
                                        <span>{selectedFolderName}</span>
                                    </div>
                                    <Close
                                        color="gray"
                                        className="cursor-pointer"
                                        onClick={onResetFiles}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
                {files.length > 0 && uploadType === 'file' && (
                    <div
                        className="mt-4 d-flex justify-content-center"
                        style={{
                            marginTop: 12,
                        }}
                    >
                        <div
                            className="d-flex justify-content-center cursor-pointer align-items-center gap-1"
                            style={{
                                width: 'fit-content',
                                opacity: 0.6,
                                fontSize: 14,
                            }}
                            onClick={onImportClick}
                        >
                            <Add />
                            <p className="mb-0">Add more file</p>
                        </div>
                    </div>
                )}

                {uploadType === 'file' ? (
                    <input
                        type="file"
                        onChange={onImportFile}
                        ref={hiddenFileInput}
                        multiple
                        style={{ display: 'none' }}
                    />
                ) : (
                    <input
                        type="file"
                        directory=""
                        webkitdirectory=""
                        onChange={onImportFile}
                        ref={hiddenFileInput}
                        style={{ display: 'none' }}
                    />
                )}

                <div className="d-flex justify-content-end mt-5">
                    <Button
                        onClick={onUploadFiles}
                        disabled={files.length === 0}
                    >
                        {isLoading ? <Spinner size="sm" /> : 'Upload'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

const mapStateToProps = (appState) => ({
    session: appState.session,
});

export default connect(mapStateToProps)(UploadElementModal);
